import { AdVertical } from "../../AdVerticals";
import {
  AD_PACKAGE_CONSTRAINT_CONFIGS,
  AdPackageConstraintsConfig,
} from "./AdPackageConstraintsConfig";
import { AdPackageKey } from "../schema/AdPackageKey";
import { AdUpgradeChoiceKey } from "../schema/AdProductChoiceKey";
import { AdProductConfigBase } from "./AdProductConfig";
import { AD_UPGRADE_CONFIGS } from "./AdUpgradeConfig";
import _ from "lodash";
import { AdType } from "../../AdTypes";
import { AdPriceType } from "../../AdPriceTypes";
import { AdPricingModel } from "../../AdConfigState";

export interface AdPackageConfig extends AdProductConfigBase {
  type: "PACKAGE";
  key: AdPackageKey;
  features: string[];
  constraints: AdPackageConstraintsConfig;
  inclusions: Array<AdUpgradeChoiceKey>;
  value: number;
  prices: Record<AdVertical, number>;
  pricesFreeConditions?: Array<{
    vertical: AdVertical;
    maxPrice: number;
  }>;
  includedValue: number;
  previousPackage: AdPackageConfig | null;
}

interface AdPackagePricingRange {
  min: number;
  max: number;
  prices: Record<AdPackageKey, number>;
}

export interface AdPackagePricing {
  adVerticals: AdVertical[];
  adPricingModels: AdPricingModel[];
  adTypes: AdType[];
  adPriceTypes: AdPriceType[];
  adPriceRanges: AdPackagePricingRange[];
}

const AD_PACKAGE_PRICING_LEVELS = [
  "lowest",
  "low",
  "mid",
  "high",
  "higher",
  "highest",
] as const;
type AdPackagePricingLevel = (typeof AD_PACKAGE_PRICING_LEVELS)[number];
const PRICING_LEVEL_PACKAGES: Record<
  AdPackagePricingLevel,
  Record<AdPackageKey, number>
> = {
  lowest: {
    BASIC: 0,
    GENERAL: 39,
    PREMIUM: 59,
    PLATINUM: 99,
  },
  low: {
    BASIC: 29,
    GENERAL: 59,
    PREMIUM: 89,
    PLATINUM: 129,
  },
  mid: {
    BASIC: 49,
    GENERAL: 79,
    PREMIUM: 99,
    PLATINUM: 149,
  },
  high: {
    BASIC: 69,
    GENERAL: 89,
    PREMIUM: 129,
    PLATINUM: 199,
  },
  higher: {
    BASIC: 89,
    GENERAL: 119,
    PREMIUM: 149,
    PLATINUM: 249,
  },
  highest: {
    BASIC: 109,
    GENERAL: 149,
    PREMIUM: 199,
    PLATINUM: 299,
  },
};

const PRICING_LEVEL_MINIMUMS: Record<
  AdVertical,
  Record<AdPackagePricingLevel, number>
> = {
  horse: {
    lowest: 0,
    low: 5000,
    mid: 10000,
    high: 20000,
    higher: 30000,
    highest: 50000,
  },
  vehicle: {
    lowest: 0,
    low: 10000,
    mid: 25000,
    high: 50000,
    higher: 75000,
    highest: 150000,
  },
  "saddlery-and-more": {
    lowest: 0,
    low: 0,
    mid: 0,
    high: 0,
    higher: 0,
    highest: 0,
  },
  property: {
    lowest: 0,
    low: 0,
    mid: 0,
    high: 0,
    higher: 0,
    highest: 0,
  },
};

function GenerateAdPackagePricingRanges(
  vertical: AdVertical,
): AdPackagePricingRange[] {
  return AD_PACKAGE_PRICING_LEVELS.map((level, index) => {
    const priceMinimums = PRICING_LEVEL_MINIMUMS[vertical];
    const nextLevel = AD_PACKAGE_PRICING_LEVELS[index + 1];
    return {
      min: priceMinimums[level],
      max: nextLevel ? priceMinimums[nextLevel] - 1 : Infinity,
      prices: PRICING_LEVEL_PACKAGES[level],
    };
  });
}

export const AD_PACKAGE_PRICING: AdPackagePricing[] = [
  {
    adVerticals: ["horse"],
    adPricingModels: ["standard"],
    adTypes: ["sale"],
    adPriceTypes: ["amount", "negotiable", "or-near-offer"],
    adPriceRanges: GenerateAdPackagePricingRanges("horse"),
  },
  {
    adVerticals: ["horse", "vehicle"],
    adPricingModels: ["standard"],
    adTypes: ["sale"],
    adPriceTypes: ["poa"],
    adPriceRanges: [
      {
        min: 0,
        max: Infinity,
        prices: PRICING_LEVEL_PACKAGES.highest,
      },
    ],
  },
  {
    adVerticals: ["horse", "vehicle"],
    adPricingModels: ["standard"],
    adTypes: ["sale"],
    adPriceTypes: ["free"],
    adPriceRanges: [
      {
        min: 0,
        max: Infinity,
        prices: PRICING_LEVEL_PACKAGES.lowest,
      },
    ],
  },
  {
    adVerticals: ["vehicle"],
    adPricingModels: ["standard"],
    adTypes: ["sale"],
    adPriceTypes: ["amount", "negotiable", "or-near-offer"],
    adPriceRanges: GenerateAdPackagePricingRanges("vehicle"),
  },
];

export const AD_BASIC_PACKAGE_CONFIG: AdPackageConfig = {
  type: "PACKAGE",
  key: "BASIC",
  title: "Basic",
  description: "1x views",
  features: [
    "Unlimited description",
    `${AD_PACKAGE_CONSTRAINT_CONFIGS.BASIC.maxImages} photos & videos`,
    `${AD_PACKAGE_CONSTRAINT_CONFIGS.BASIC.maxDisciplines} disciplines`,
    "Pedigree tree",
  ],
  constraints: AD_PACKAGE_CONSTRAINT_CONFIGS.BASIC,
  inclusions: [],
  value: 1,
  prices: {
    horse: 29,
    vehicle: 29,
    "saddlery-and-more": 29,
    property: 99,
  },
  pricesFreeConditions: [
    { vertical: "horse", maxPrice: 5000 },
    { vertical: "vehicle", maxPrice: 10000 },
    { vertical: "saddlery-and-more", maxPrice: 3000 },
  ],
  previousPackage: null,
  includedValue: 0,
};

const AD_BASIC_UNLIMITED_MAX_IMAGES_OVERRIDE = 20;
export const AD_BASIC_UNLIMITED_PACKAGE_CONFIG: AdPackageConfig = {
  ...AD_BASIC_PACKAGE_CONFIG,
  features: [
    "Unlimited description",
    `${AD_BASIC_UNLIMITED_MAX_IMAGES_OVERRIDE} photos & videos`,
    `${AD_PACKAGE_CONSTRAINT_CONFIGS.BASIC.maxDisciplines} disciplines`,
    "Pedigree tree",
  ],
  constraints: {
    ...AD_PACKAGE_CONSTRAINT_CONFIGS.BASIC,
    maxImages: AD_BASIC_UNLIMITED_MAX_IMAGES_OVERRIDE,
  },
  prices: {
    horse: 0,
    vehicle: 0,
    "saddlery-and-more": 0,
    property: 0,
  },
};

const AD_GENERAL_PACKAGE_INCLUSIONS: Array<AdUpgradeChoiceKey> = [
  { type: "UPGRADE", product: "SEARCH_FEATURE", variant: "30_DAYS" },
  { type: "UPGRADE", product: "HIGHLIGHT", variant: "30_DAYS" },
];

const AD_PREMIUM_PACKAGE_INCLUSIONS: Array<AdUpgradeChoiceKey> = [
  ...AD_GENERAL_PACKAGE_INCLUSIONS,
  { type: "UPGRADE", product: "HOMEPAGE_SHOWREEL", variant: "15_DAYS" },
];

const AD_PLATINUM_PACKAGE_INCLUSIONS: Array<AdUpgradeChoiceKey> = [
  ...AD_PREMIUM_PACKAGE_INCLUSIONS,
  { type: "UPGRADE", product: "EMAIL_NEWSLETTER", variant: "STANDARD" },
];

function CalculateIncludedValue(inclusions: Array<AdUpgradeChoiceKey>): number {
  const inclusionsPrices = inclusions.map((inclusion) => {
    const upgradeConfig = AD_UPGRADE_CONFIGS[inclusion.product];
    const upgradeVariant = upgradeConfig.variants.find(
      (variant) => variant.key === inclusion.variant,
    );
    return upgradeVariant?.price;
  });
  return _.sum(inclusionsPrices);
}

const AD_GENERAL_PACKAGE_CONFIG: AdPackageConfig = {
  type: "PACKAGE",
  key: "GENERAL",
  title: "General",
  description: "2x views",
  features: [
    `${AD_PACKAGE_CONSTRAINT_CONFIGS.GENERAL.maxImages} photos & videos`,
    `${AD_PACKAGE_CONSTRAINT_CONFIGS.GENERAL.maxDisciplines} disciplines`,
  ],
  constraints: AD_PACKAGE_CONSTRAINT_CONFIGS.GENERAL,
  inclusions: AD_GENERAL_PACKAGE_INCLUSIONS,
  value: 2,
  prices: {
    horse: 59,
    vehicle: 59,
    "saddlery-and-more": 59,
    property: 149,
  },
  previousPackage: AD_BASIC_PACKAGE_CONFIG,
  includedValue: CalculateIncludedValue(AD_GENERAL_PACKAGE_INCLUSIONS),
};

const AD_PREMIUM_PACKAGE_CONFIG: AdPackageConfig = {
  type: "PACKAGE",
  key: "PREMIUM",
  title: "Premium",
  description: "3x views",
  features: ["Unlimited photos & videos"],
  constraints: AD_PACKAGE_CONSTRAINT_CONFIGS.PREMIUM,
  inclusions: AD_PREMIUM_PACKAGE_INCLUSIONS,
  value: 3,
  prices: {
    horse: 89,
    vehicle: 89,
    "saddlery-and-more": 89,
    property: 199,
  },
  previousPackage: AD_GENERAL_PACKAGE_CONFIG,
  includedValue: CalculateIncludedValue(AD_PREMIUM_PACKAGE_INCLUSIONS),
};

const AD_PLATINUM_PACKAGE_CONFIG: AdPackageConfig = {
  type: "PACKAGE",
  key: "PLATINUM",
  title: "Platinum",
  description: "5x views",
  features: ["VIP Customer Care", "Pause your ad anytime"],
  constraints: AD_PACKAGE_CONSTRAINT_CONFIGS.PLATINUM,
  inclusions: AD_PLATINUM_PACKAGE_INCLUSIONS,
  value: 4,
  prices: {
    horse: 149,
    vehicle: 149,
    "saddlery-and-more": 149,
    property: 299,
  },
  previousPackage: AD_PREMIUM_PACKAGE_CONFIG,
  includedValue: CalculateIncludedValue(AD_PLATINUM_PACKAGE_INCLUSIONS),
};

export const AD_PACKAGE_CONFIGS: Record<AdPackageKey, AdPackageConfig> = {
  BASIC: AD_BASIC_PACKAGE_CONFIG,
  GENERAL: AD_GENERAL_PACKAGE_CONFIG,
  PREMIUM: AD_PREMIUM_PACKAGE_CONFIG,
  PLATINUM: AD_PLATINUM_PACKAGE_CONFIG,
};

export const AD_PACKAGE_DEFAULT: AdPackageConfig = AD_PREMIUM_PACKAGE_CONFIG;
